/**
 * The card drawn from a lesson lifts out of the fan and settles into its panel
 * (lesson draw presentation, 2026-10-28). A face-down ghost of the picked card
 * rises where it lay, waits (breathing) for the panel's art slot to be in
 * place and still, then flies there and ends edge-on, so the panel's own turn
 * (TalkCard, rotateY 90° → 0) finishes the flip face up.
 *
 * `from`: { left, top, width, height } of the picked card on screen (its
 * bounding box), plus `cardWidth`, `cardHeight`, `angle` when it lay rotated
 * in the fan. `target()`: the art slot, or null until it exists.
 * `onLanded()` is called once: landed, given up, cancelled, or at once when
 * there is no motion to make (reduced motion, no start, no Web Animations).
 */
const LIFT_MS = 420
const FLY_MS = 780
const LIFT_Y = -22
const PERSPECTIVE = 'perspective(800px)'

const tf = ({ x = 0, y = 0, z = 0, sx = 1, sy = 1, ry = 0 } = {}) =>
  `${PERSPECTIVE} translate(${x}px, ${y}px) rotate(${z}deg) scale(${sx}, ${sy}) rotateY(${ry}deg)`

export function settleCard ({ from, image = '', target, reduced = false, onLanded, notBefore = 0, giveUpMs = 9000 } = {}) {
  let done = false
  let ghost = null
  const land = () => {
    if (done) return
    done = true
    if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost)
    if (typeof onLanded === 'function') onLanded()
  }
  const none = { cancel: land }
  if (reduced || !from || !from.width || !from.height || typeof document === 'undefined') { land(); return none }
  const probe = document.createElement('div')
  if (typeof probe.animate !== 'function' || typeof requestAnimationFrame !== 'function') { land(); return none }

  const w = from.cardWidth || from.width
  const h = from.cardHeight || from.height
  const cx = from.left + from.width / 2
  const cy = from.top + from.height / 2
  ghost = probe
  ghost.className = 'ldp-ghost'
  ghost.setAttribute('aria-hidden', 'true')
  ghost.style.cssText = `position:fixed;left:${cx - w / 2}px;top:${cy - h / 2}px;width:${w}px;height:${h}px;`
    + 'z-index:2000;pointer-events:none;border-radius:8px;overflow:hidden;transform-origin:50% 50%;will-change:transform;'
    + 'box-shadow:0 0 28px rgba(255,223,163,.55),0 16px 34px rgba(0,0,0,.6);background:#1c1030;'
  if (image) {
    const img = document.createElement('img')
    img.src = image
    img.alt = ''
    img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block'
    ghost.appendChild(img)
  }
  document.body.appendChild(ghost)
  const a0 = Number(from.angle) || 0
  const lifted = { y: LIFT_Y, sx: 1.08, sy: 1.08 }
  ghost.animate([{ transform: tf({ z: a0 }) }, { transform: tf(lifted) }],
    { duration: LIFT_MS, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' })

  const t0 = performance.now()
  let last = null
  let still = 0
  const fly = (r) => {
    const dx = r.left + r.width / 2 - cx
    const dy = r.top + r.height / 2 - cy
    const sx = r.width / w
    const sy = r.height / h
    const anim = ghost.animate([
      { transform: tf(lifted), offset: 0 },
      { transform: tf({ x: dx * 0.5, y: dy * 0.5 + LIFT_Y - 26, sx: (1.08 + sx) / 2, sy: (1.08 + sy) / 2 }), offset: 0.5 },
      { transform: tf({ x: dx, y: dy, sx, sy }), offset: 0.78 },
      { transform: tf({ x: dx, y: dy, sx, sy, ry: 90 }), offset: 1 },
    ], { duration: FLY_MS, easing: 'cubic-bezier(.45,0,.25,1)', fill: 'forwards' })
    anim.onfinish = land
    anim.oncancel = land
  }
  const seek = () => {
    if (done) return
    const now = performance.now()
    if (now - t0 > giveUpMs) {
      const out = ghost.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, fill: 'forwards' })
      out.onfinish = land
      return
    }
    const el = typeof target === 'function' ? target() : null
    if (!el || now < t0 + Math.max(LIFT_MS, notBefore)) { requestAnimationFrame(seek); return }
    const r = el.getBoundingClientRect()
    if (!r.width || !r.height) { requestAnimationFrame(seek); return }
    // The slot must be still (the pane's split and the chat's scroll settle first).
    if (!last || Math.abs(last.left - r.left) > 0.5 || Math.abs(last.top - r.top) > 0.5) { last = r; still = 0; requestAnimationFrame(seek); return }
    if (++still < 4) { requestAnimationFrame(seek); return }
    fly(r)
  }
  requestAnimationFrame(seek)
  return { cancel: land }
}
